import type { ModelSelection, ThreadProviderRateLimitStatus } from "@cmmd-forge/contracts";

import type { ProviderUsageAccountView } from "~/usage/useProviderUsage";
import { isSubscriptionProvider, resolveComposerUsageAccount } from "../composerPlanUsage";

export interface ComposerUsageNoticeContext {
  readonly threadId: string;
  readonly modelSelection: ModelSelection | null;
  readonly rateLimitStatus: ReadonlyArray<ThreadProviderRateLimitStatus> | undefined;
  readonly latestTurnId: string | null;
  readonly pendingInputId: string | null;
}

export interface ComposerUsageNoticeContextIdentity {
  readonly key: string;
  readonly baseKey: string;
  readonly accountResolved: boolean;
  /** An explicit picker change must never look like the first inventory result. */
  readonly selectedAccountId: string | null;
}

export function composerUsageNoticeContextIdentity(
  context: ComposerUsageNoticeContext,
  usageAccounts: ReadonlyArray<ProviderUsageAccountView>,
  accountsResolved: boolean,
): ComposerUsageNoticeContextIdentity | null {
  const selection = context.modelSelection;
  if (!selection || !isSubscriptionProvider(selection.provider)) return null;
  const selectedAccountId =
    "providerAccountId" in selection ? selection.providerAccountId : undefined;
  const account = resolveComposerUsageAccount({
    modelSelection: selection,
    usageAccounts,
  })?.effectiveAccountId;
  const baseKey = [
    context.threadId,
    selection.provider,
    context.latestTurnId ?? "none",
    context.pendingInputId ?? "none",
  ].join("\u0000");
  return {
    key: [baseKey, account ?? "default"].join("\u0000"),
    baseKey,
    accountResolved: selectedAccountId !== undefined || accountsResolved,
    selectedAccountId: selectedAccountId ?? null,
  };
}

export function composerUsageNoticeContextKey(
  context: ComposerUsageNoticeContext,
  usageAccounts: ReadonlyArray<ProviderUsageAccountView> = [],
  accountsResolved = usageAccounts.length > 0,
): string | null {
  return composerUsageNoticeContextIdentity(context, usageAccounts, accountsResolved)?.key ?? null;
}
